import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { Link } from "react-router-dom";
import { formatPrice, statusMeta } from "../mock/mock";
import { mediaUrl, thumbUrl } from "../lib/api";

const COLORS = { achat: "#1156a7", location: "#2f74c9" };

const makeIcon = (listing) => {
  const color = COLORS[listing] || COLORS.achat;
  return L.divIcon({
    className: "bella-pin",
    html: `<div style="
      width:26px;height:26px;border-radius:50% 50% 50% 0;
      background:${color};transform:rotate(-45deg);
      border:2.5px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,.35);
      display:flex;align-items:center;justify-content:center;">
      <span style="width:8px;height:8px;background:#fff;border-radius:50%;transform:rotate(45deg);"></span>
    </div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });
};

const makeClusterIcon = (cluster) => {
  const n = cluster.getChildCount();
  const size = n < 10 ? 38 : n < 50 ? 46 : 54;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#0b1f33;color:#fff;
      display:flex;align-items:center;justify-content:center;font-weight:700;font-size:${n < 100 ? 14 : 12}px;
      border:3px solid #2f74c9;box-shadow:0 4px 14px rgba(11,31,51,.35);font-family:Montserrat,sans-serif;">${n}</div>`,
    className: "bella-cluster",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => `${p[0].toFixed(3)},${p[1].toFixed(3)}`).join("|");
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      map.fitBounds(points, { padding: [50, 50], maxZoom: 14 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

const validCoord = (p) =>
  typeof p.lat === "number" && typeof p.lng === "number" &&
  Number.isFinite(p.lat) && Number.isFinite(p.lng) &&
  Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180 && !(p.lat === 0 && p.lng === 0);

// Many listings share a zone centroid; spread duplicates on a small ring so every pin stays clickable.
function spreadPins(items) {
  const groups = new Map();
  items.forEach((p) => {
    const k = `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`;
    groups.set(k, [...(groups.get(k) || []), p]);
  });
  const out = [];
  groups.forEach((g) => {
    if (g.length === 1) { out.push({ ...g[0], _lat: g[0].lat, _lng: g[0].lng }); return; }
    g.forEach((p, i) => {
      const ring = Math.floor(i / 8) + 1;
      const angle = (i % 8) * (Math.PI / 4) + ring * 0.4;
      const r = 0.0012 * ring;
      out.push({ ...p, _lat: p.lat + r * Math.sin(angle), _lng: p.lng + r * Math.cos(angle) / Math.cos((p.lat * Math.PI) / 180) });
    });
  });
  return out;
}

export default function PropertyMap({ properties = [], onSelect, height = "440px" }) {
  const withCoords = spreadPins(properties.filter(validCoord));
  const points = withCoords.map((p) => [p._lat, p._lng]);
  const center = points[0] || [20.6296, -87.0739]; // Playa del Carmen

  return (
    <div className="relative isolate z-0 rounded-lg overflow-hidden border border-slate-200 shadow-sm" data-testid="property-map">
      <MapContainer
        center={center}
        zoom={12}
        minZoom={5}
        scrollWheelZoom={false}
        style={{ height, width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        <FitBounds points={points} />
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={makeClusterIcon}
          maxClusterRadius={60}
          disableClusteringAtZoom={16}
          spiderfyOnMaxZoom
          showCoverageOnHover={false}
          polygonOptions={{ color: "#2f74c9", weight: 1, fillOpacity: 0.05 }}
        >
        {withCoords.map((p) => (
          <Marker
            key={p.id}
            position={[p._lat, p._lng]}
            icon={makeIcon(p.listing_type)}
            eventHandlers={{ click: () => onSelect && onSelect(p) }}
          >
            <Popup>
              <div style={{ width: 210 }}>
                <img
                  src={thumbUrl(p.image, 320)}
                  loading="lazy"
                  alt={p.title}
                  style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 6 }}
                />
                <div style={{ marginTop: 8 }}>
                  <span
                    style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: ".05em",
                      color: p.listing_type === "location" ? "#2f74c9" : "#1156a7",
                      textTransform: "uppercase",
                    }}
                  >
                    {p.listing_type === "location" ? "À louer" : "À acheter"} · {statusMeta(p.status).label}
                  </span>
                  <p style={{ margin: "2px 0", fontWeight: 700, color: "#0b1f33", fontSize: 15 }}>
                    {p.title}
                  </p>
                  <p style={{ margin: 0, color: "#64748b", fontSize: 12 }}>{p.location}</p>
                  <p style={{ margin: "6px 0", fontWeight: 700, color: "#1156a7" }}>
                    {formatPrice(p.price, p.currency)}
                  </p>
                  <Link
                    to={`/proprietes/${p.id}`}
                    style={{
                      display: "inline-block", background: "#1156a7", color: "#fff",
                      fontSize: 12, fontWeight: 600, padding: "6px 14px", borderRadius: 4,
                      textDecoration: "none",
                    }}
                  >
                    Voir la fiche
                  </Link>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
        </MarkerClusterGroup>
      </MapContainer>

      {/* legend */}
      <div className="absolute bottom-4 right-4 z-[500] bg-white/95 rounded-md shadow px-4 py-3 text-xs space-y-1.5">
        <p className="text-[10px] text-slate-400 uppercase tracking-wider">{withCoords.length} propriétés sur la carte</p>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ background: COLORS.achat }} /> À acheter
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ background: COLORS.location }} /> À louer
        </div>
      </div>
    </div>
  );
}
